import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, Chip, CircularProgress, Typography} from "@mui/material";
import {PublishedWithChangesOutlined, WarningAmberOutlined} from "@mui/icons-material";

import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {useSessionContext} from "../../util/SessionContext";
import {approvalMessage} from "../../util/approvalMessages";
import {formatMoment} from "../../util/formatMoment";
import ApprovalSignatureDialog from "./ApprovalSignatureDialog";
import {signableSlots} from "../../util/approvalSlots";
import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";
import {useNavigate} from "react-router";
import {asObligation} from "./SlotState";
import ApprovalDiagram from "./ApprovalDiagram";
import RetrainingDialog from "./RetrainingDialog";

// The approval surface of one governed record -- the fifth tab beside Detail, Stewardship,
// Permissions and History, and the one most people will actually meet.
//
// WHEN NOTHING IS REQUIRED IT SAYS SO IN WORDS. An empty panel would read as "not loaded yet", and
// the fact that a record type releases unwitnessed is exactly the thing somebody should be able to
// notice. That is the coverage message from the matrix grid, delivered where it can be acted on.
//
// Design: cres-t5/docs/approvals/design.md

type Row = Record<string, any>;

/** The request this tab is about: the open one, else the most recently decided. */
export function currentRequest(requests: Row[]) {
    const pending = (requests ?? []).find(one => one.Status === "PENDING");
    if (pending) return pending;
    return (requests ?? [])[0] ?? null;
}

/**
 * The stepper: one entry per stage, in order, with how far each got.
 *
 * A stage EARLIER than the open one is done; the open one is in progress; later ones are pending.
 * Deriving it from CurrentStage rather than from signature counts matters for quorum: a 1-of-3
 * group leaves two slots unsigned forever, so counting signatures would show a satisfied stage as
 * incomplete.
 */
export function stageProgress(request: Row | null) {
    if (!request) return [];
    const slots = request.Slots ?? [];
    const stages = [...new Set(slots.map((slot: Row) => slot.Stage ?? 1))].sort((a: any, b: any) => a - b);

    return stages.map((stage: any) => {
        const inStage = slots.filter((slot: Row) => (slot.Stage ?? 1) === stage);
        const signed = inStage.filter((slot: Row) => slot.Signature).length;
        const open = request.Status === "PENDING" && stage === request.CurrentStage;
        return {
            stage,
            label: inStage[0]?.StageLabel ?? null,
            signed,
            total: inStage.length,
            state: request.Status === "APPROVED" || stage < request.CurrentStage ? "done"
                 : open ? "open" : "pending",
        };
    });
}

export default function ApprovalTab({targetType, recordUSID, recordStatus,
                                     showClass = true, onState, onChanged}: any) {
    const navigate = useNavigate();
    const [state, setState] = useState<Row | null>(null);
    // BUILT FROM THE SESSION, as every t1 screen does: the token lives on the
    // session context and there is no module-level instance to import here.
    const sessionContext = useSessionContext();
    const api = React.useMemo(
        () => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [signing, setSigning] = useState<Row | null>(null);
    // Open only when the server has told us this submission needs the retraining answer.
    const [asking, setAsking] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api({method: "GET", url: "t1/v1/object_approval_requests",
                       params: {TargetType: targetType, RecordUSID: recordUSID}}),
        ])
            .then(([approval]) => {
                setState(approval.data ?? null);
                // REPORTED UP so a surrounding page can say the record is out for approval without
                // fetching the same thing twice. The matrix page needs it: its header said DRAFT
                // whether or not the draft had been submitted, and the only clue was that the
                // Submit button had quietly become a Withdraw link further down the page.
                if (onState) onState(approval.data ?? null);
            })
            .catch(error => setMessage(approvalMessage(error, "Could not load the approval state.")))
            .finally(() => setLoading(false));
    }, [api, targetType, recordUSID, onState]);

    useEffect(() => { load(); }, [load]);

    const act = (call: Promise<any>, failure: string) => {
        setBusy(true);
        setMessage(null);
        // THE RECORD CHANGED, not just this tab. A final signature moves the record's own
        // ApprovalStatus, and a page showing that field beside this tab would go on showing the old
        // value until something else made it reload. Told after the reload, so the caller re-reads a
        // record the server has already finished updating.
        call.then(() => { setSigning(null); load(); if (onChanged) onChanged(); })
            .catch(error => setMessage(approvalMessage(error, failure)))
            .finally(() => setBusy(false));
    };

    // SUBMIT, AND THE ONE QUESTION SUBMITTING CAN ASK BACK.
    //
    // A revision whose Training Setup says Require Retraining is on cannot be submitted without
    // saying whether THIS change requires people to retrain. The question used to be asked at
    // RELEASE -- but under an approval matrix release is not an act somebody performs, it is what
    // happens on the final signature, so it landed on an approver with nowhere to answer it: Adaeze
    // Nwosu signed SOP-00001 and the sign panel stayed open on an error she could do nothing about.
    //
    // ASKED ON REFUSAL RATHER THAN UP FRONT, because this tab is given the record's status and not
    // its training settings, and the server already knows. One wasted round trip on the first
    // submit of a retraining record buys a tab that cannot fall out of step with the rule, and
    // CC-OAPR-RTRN survives prod stripping `Cause` -- the Code is all this needs.
    const submit = (answer?: boolean) => {
        setBusy(true);
        setMessage(null);
        api({method: "POST", url: "t1/v1/object_approval_requests",
             data: {TargetType: targetType, RecordUSID: recordUSID,
                    ...(answer === undefined ? {} : {TrainingResetRequired: answer})}})
            .then(() => { setAsking(false); load(); if (onChanged) onChanged(); })
            .catch((error: any) => {
                if (error?.response?.data?.Code === "CC-OAPR-RTRN") setAsking(true);
                else setMessage(approvalMessage(error, "Could not submit this for approval."));
            })
            .finally(() => setBusy(false));
    };

    if (loading) return <Box sx={{padding: "24px"}}><CircularProgress size={20}/></Box>;

    const request = currentRequest(state?.Requests ?? []);
    // The package this record was approved through, if any. Taken from the request being shown
    // rather than from the list, so the banner always describes the signatures actually on screen.
    const viaChangeOrder = request?.ViaTargetType === "ChangeOrder" ? request : null;
    const stages = stageProgress(request);
    const mine = signableSlots(request);
    const isDraft = recordStatus === "DRAFT";

    return (
        <div style={{margin: "20px 0 40px 0", maxWidth: 900}}>
            {message ? (
                <Typography style={{fontSize: 13, color: "#B42318", marginBottom: 12}}>{message}</Typography>
            ) : null}

            {/* NO OBJECT-TYPE PICKER, and that is the tier difference. In the Catalog a record's
                governance class is a curatorial judgement made on the record, so the approval tab
                let you set it. A Foundation object's type is structural -- it is what the object IS,
                chosen when the object was created and joined through OBJECT -- so there is nothing
                here to choose. The server has no classify action to call. We report what the type
                resolved to and leave it at that. */}
            {showClass && state?.ResolvedBy ? (
                <Typography style={{fontSize: 12, color: Grey6, marginBottom: 14}}>
                    Approvers resolved by {state.ResolvedBy}
                </Typography>
            ) : null}

            {/* APPROVED THROUGH A PACKAGE, SAID OUT LOUD. A record inside a change order is never
                submitted on its own: the package is submitted, the signatures are given against it,
                and closing it releases each member. Without this line the tab shows signatures whose
                slots name approvers the reader never expected, against a request they cannot find
                from here -- or, before the fix behind it, showed nothing at all and left "Required,
                two stages" sitting over an empty panel for the whole life of the record.

                THE LINK IS THE POINT. An auditor asking who approved this revision needs to reach
                the package, and this is the only place on the record that knows. */}
            {viaChangeOrder ? (
                <Box sx={{border: "1px solid #D8DDE4", borderRadius: "8px", padding: "10px 14px",
                          marginBottom: 14, display: "flex", alignItems: "center", gap: 8}}>
                    <PublishedWithChangesOutlined sx={{fontSize: 18, color: Blue3}}/>
                    <Typography style={{fontSize: 13, color: Grey17}}>
                        Approved as part of{" "}
                        <span onClick={() => navigate(`/change-orders/${viaChangeOrder.ViaRecordUSID}`)}
                              style={{color: Blue3, cursor: "pointer", fontWeight: 500}}>
                            {viaChangeOrder.ViaRecordID}
                        </span>
                        {viaChangeOrder.ViaRecordName ? ` — ${viaChangeOrder.ViaRecordName}` : ""}
                        {". "}
                        The signatures below were given against that change order, not against this
                        record on its own.
                    </Typography>
                </Box>
            ) : null}

            {!state?.Required ? (
                <Box sx={{border: "1px dashed #D8DDE4", borderRadius: "8px", padding: "24px"}}>
                    <Typography style={{fontSize: 14, color: Grey5}}>
                        {state?.Reason ?? "No approvers are configured, so this releases directly."}
                    </Typography>
                </Box>
            ) : (
                <>
                    {stages.length > 1 ? (
                        <div style={{display: "flex", gap: 18, marginBottom: 16, flexWrap: "wrap"}}>
                            {stages.map(step => (
                                <Typography key={step.stage}
                                            style={{fontSize: 13,
                                                    color: step.state === "done" ? "#067647"
                                                         : step.state === "open" ? undefined : Grey6,
                                                    fontWeight: step.state === "open" ? 600 : 400}}>
                                    {step.state === "done" ? "✓" : step.state === "open" ? "●" : "○"}{" "}
                                    Stage {step.stage}{step.label ? ` — ${step.label}` : ""}{" "}
                                    ({step.signed} of {step.total})
                                </Typography>
                            ))}
                        </div>
                    ) : null}

                    {request?.IsBlocked ? (
                        <Typography style={{fontSize: 13, color: "#B54708", marginBottom: 12,
                                            display: "flex", alignItems: "center", gap: 4}}>
                            <WarningAmberOutlined sx={{fontSize: 16}}/>{request.BlockedReason}
                        </Typography>
                    ) : null}

                    {/* THE PICTURE FIRST, THEN THE DETAIL. The diagram answers "where is this and
                        what is it waiting for"; the rows below answer "who exactly, and when". */}
                    <ApprovalDiagram request={request}/>

                    {request ? (
                        <div style={{border: "1px solid #E4E7EC", borderRadius: 6, marginBottom: 14}}>
                            {(request.Slots ?? []).map((slot: Row) => (
                                <div key={slot.USID}
                                     style={{padding: "10px 14px", borderTop: "1px solid #F2F4F7",
                                             display: "flex", alignItems: "center", gap: 10}}>
                                    <Typography style={{fontSize: 13,
                                                        color: slot.Signature ? "#067647" : Grey6}}>
                                        {slot.Signature ? "✓" : "○"}
                                    </Typography>
                                    <Typography style={{fontSize: 13, minWidth: 200}}>
                                        {slot.PersonaName}
                                    </Typography>
                                    {/* THE TENSE CARRIES THE STATE. A bare Meaning chip said
                                        "Approved" on a slot nobody had signed, which reads as a
                                        record that they did. Unsigned slots now say what is OWED. */}
                                    <Chip size="small"
                                          label={slot.Signature
                                              ? (String(slot.Signature.Decision ?? "").toUpperCase().startsWith("REJECT")
                                                  ? "rejected" : (slot.Meaning ?? "signed").toLowerCase())
                                              : asObligation(slot.Meaning ?? null)}
                                          variant={slot.Signature ? "filled" : "outlined"}
                                          sx={{fontSize: 11, height: 20,
                                               ...(slot.Signature
                                                   ? {} : {color: Grey6, borderStyle: "dashed"})}}/>
                                    {/* NOT OPEN YET is a different fact from NOT DONE YET, and only
                                        the first excuses the delay. */}
                                    {!slot.Signature && typeof slot.Stage === "number"
                                        && typeof request?.CurrentStage === "number"
                                        && slot.Stage > request.CurrentStage ? (
                                        <Typography style={{fontSize: 11, color: Grey6}}>
                                            stage {slot.Stage}, not open yet
                                        </Typography>
                                    ) : null}
                                    {/* "any 1 of 3", not "any 1 of governance". The group NAME is
                                        internal; what a reader needs is how many of how many. */}
                                    {slot.QuorumGroup ? (
                                        <Chip size="small"
                                              label={`any ${slot.QuorumRequired ?? 1} of `
                                                  + (request?.Slots ?? []).filter(
                                                      (one: Row) => one.QuorumGroup === slot.QuorumGroup).length}
                                              sx={{fontSize: 11, height: 20}}/>
                                    ) : null}
                                    <div style={{flex: 1}}/>
                                    {slot.Signature ? (
                                        <Typography style={{fontSize: 12, color: Grey6}}>
                                            {slot.Signature.PrintedName} · {formatMoment(slot.Signature.SignedDatetime)}
                                            {String(slot.Signature.Decision ?? "").toUpperCase().startsWith("REJECT") ? " · rejected" : ""}
                                        </Typography>
                                    ) : mine.includes(slot) ? (
                                        <Button size="small" variant="outlined" disabled={busy}
                                                onClick={() => setSigning(slot)}
                                                sx={{textTransform: "none", fontSize: 12}}>
                                            Sign as {slot.Meaning}
                                        </Button>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    ) : null}

                    <div style={{display: "flex", gap: 8}}>
                        {/* NOT WHILE ONE IS OPEN, AND NOT WHEN ONE IS ALREADY SATISFIED. The second
                            half was missing, so a fully signed record still offered Submit -- Ed
                            pressed it on SOP-00001 and got a second request beside the first, at
                            stage 1: "it indicated every one had signed and I clicked on the Submit
                            for Approval and now the approval process has started over." The server
                            refuses it now (CC-OAPR-APRV); this is the button following the rule.
                            The record is DRAFT until it is released, so isDraft cannot carry it. */}
                        {isDraft && !["PENDING", "APPROVED"].includes(request?.Status) ? (
                            <Button size="small" variant="contained" disabled={busy}
                                    onClick={() => submit()}
                                    sx={{textTransform: "none"}}>
                                Submit for Approval
                            </Button>
                        ) : null}
                        {request?.Status === "PENDING" ? (
                            <Button size="small" disabled={busy}
                                    onClick={() => act(api({
                                        method: "DELETE", url: "t1/v1/object_approval_requests",
                                        params: {USID: request.USID},
                                    }), "Could not withdraw this approval.")}
                                    sx={{textTransform: "none"}}>
                                Withdraw
                            </Button>
                        ) : null}
                        {request && request.Status !== "PENDING" ? (
                            <Typography style={{fontSize: 13, color: Grey6, alignSelf: "center"}}>
                                Last approval: {request.Status.toLowerCase()}.
                            </Typography>
                        ) : null}
                    </div>
                </>
            )}

            <RetrainingDialog open={asking} busy={busy}
                              onAnswer={(answer: boolean) => submit(answer)}
                              onClose={() => setAsking(false)}/>

            <ApprovalSignatureDialog
                open={!!signing} request={request} slot={signing} busy={busy}
                onSign={(decision: string, comment: string, password: string) => act(api({
                    method: "PUT", url: "t1/v1/object_approval_requests",
                    data: {RequestUSID: request?.USID, SlotUSID: signing?.USID,
                           Decision: decision, Comment: comment, Password: password},
                }), "Could not record that.")}
                onClose={() => setSigning(null)}/>
        </div>
    );
}
